import {useState, useLayoutEffect, useRef, RefObject} from 'react';

// -----------------------------
// Normal resize observer
// -----------------------------
interface ElementSize {
  width: number;
  height: number;
  zoom: number; // Browser-level zoom
  cssZoom: number; // Element-level CSS zoom property
}

export const useElementSize = <T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T | null>,
  ElementSize,
] => {
  const [size, setSize] = useState<ElementSize>({
    width: 0,
    height: 0,
    zoom: 1,
    cssZoom: 1,
  });
  const targetRef = useRef<T>(null);

  useLayoutEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const cssZoom = (element as any).currentCSSZoom || 1;

      setSize({
        width: rect.width,
        height: rect.height,
        zoom: window.devicePixelRatio,
        cssZoom: cssZoom,
      });
    };

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);
    window.addEventListener('resize', updateSize);

    // Initial call
    updateSize();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  return [targetRef, size];
};

// -----------------------------
// Zoom in and out aware resize observer
// -----------------------------
interface ZoomSize {
  width: number;
  height: number;
  zoomFactor: number;
}

export const useZoomAwareSize = <T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T | null>,
  ZoomSize,
] => {
  const [size, setSize] = useState<ZoomSize>({
    width: 0,
    height: 0,
    zoomFactor: 1,
  });
  const targetRef = useRef<T>(null);

  useLayoutEffect(() => {
    const element = targetRef.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      const zoom = window.devicePixelRatio || 1;

      setSize({
        // Multiply by zoom to get the proper values during zoom out
        width: rect.width,
        height: rect.height,
        zoomFactor: zoom,
      });
    };

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    window.addEventListener('resize', updateSize);

    updateSize();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  return [targetRef, size];
};
// ----------------------------
